/**
 * Auth Context & Provider
 * 
 * Provides authentication state throughout the React app.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService, type AuthState } from '../services/AuthService';
import type { User, AuthError } from '@supabase/supabase-js';

interface OrgContext {
  orgId: string;
  orgName: string;
  orgSlug: string;
  orgRole: 'owner' | 'admin' | 'member' | 'viewer';
}

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, captchaToken?: string) => Promise<{ error: AuthError | null }>;
  signInWithMagicLink: (email: string) => Promise<{ error: AuthError | null }>;
  signInWithGoogle: () => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
  resendConfirmation: (email: string) => Promise<{ error: AuthError | null }>;
  org: OrgContext | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>(authService.getState());
  const [org, setOrg] = useState<OrgContext | null>(null);

  useEffect(() => {
    const unsubscribe = authService.subscribe(setState);
    return unsubscribe;
  }, []);

  // Fetch org membership when user changes
  useEffect(() => {
    if (!state.user) {
      setOrg(null);
      return;
    }
    const fetchOrg = async () => {
      try {
        const { getApiBase } = await import('../config/api');
        const { supabase } = await import('../lib/supabase');
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const res = await fetch(`${getApiBase()}/api/organizations/me`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.organization) {
          setOrg({
            orgId: data.organization.id,
            orgName: data.organization.name,
            orgSlug: data.organization.slug,
            orgRole: data.organization.role,
          });
        } else {
          setOrg(null);
        }
      } catch {
        setOrg(null);
      }
    };
    fetchOrg();
  }, [state.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await authService.signIn(email, password);
    return { error };
  }, []);

  const signUp = useCallback(async (email: string, password: string, captchaToken?: string) => {
    const { error } = await authService.signUp(email, password, captchaToken);
    return { error };
  }, []);

  const signInWithMagicLink = useCallback(async (email: string) => {
    return authService.signInWithMagicLink(email);
  }, []);

  const signInWithGoogle = useCallback(async () => {
    return authService.signInWithProvider('google');
  }, []);

  const signOut = useCallback(async () => {
    return authService.signOut();
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    return authService.resetPassword(email);
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    return authService.updatePassword(newPassword);
  }, []);

  const resendConfirmation = useCallback(async (email: string) => {
    return authService.resendConfirmation(email);
  }, []);

  const value: AuthContextType = {
    ...state,
    signIn,
    signUp,
    signInWithMagicLink,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
    resendConfirmation,
    org,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook to get current user (convenience)
 */
export function useUser(): User | null {
  const { user } = useAuth();
  return user;
}

/**
 * Hook to check if user is authenticated
 */
export function useIsAuthenticated(): boolean {
  const { user, loading } = useAuth();
  return !loading && !!user;
}

/**
 * Hook to get current org context (convenience)
 */
export function useOrg(): OrgContext | null {
  const { org } = useAuth();
  return org;
}

export default AuthContext;
